/**
 * A faixa viva (§12.5; ORD-F-133): lê a peça da faixa, valida e desenha o PartnerSlot `bar` na caixa reservada pelo
 * slot (app/partnerSlot.tsx), no `index` (sem pedaço próprio: DECISOES §77). Só monta com `features.partner_slots`.
 *
 * - Fonte: `/partner/partner.json` da própria origem do site (contrato `ops.partner`, src/contracts/partner.ts), com a
 *   imagem em `/partner/`. Nada de terceiro: nem script, nem cookie, nem pixel, nem imagem de outra origem (a CSP
 *   `img-src 'self'` também barra). O motor não publica este arquivo (feed_publico/contrato.py: estático do front).
 * - Quando: na apuração, depois do 1º snapshot desenhado (`liveView` e a 1ª pintura); na página inicial e nas
 *   pré-apurações (sem feed), depois da 1ª pintura (ORD-F-136). Sempre em `requestIdleCallback` (fora do caminho crítico,
 *   M1), e de novo a cada 30 min. A validade (`valid_from`/`valid_until`) é conferida a cada minuto.
 * - Arquivo retirado ou recusado (404, contrato inválido, `enabled = false`, fora da validade, imagem de fora de
 *   `/partner/`) deixa a faixa desligada, sem mensagem.
 * - Falha passageira na releitura (rede fora, 5xx, resposta cortada) mantém a última peça boa, que segue presa à
 *   validade (pré-mortem de 25/10, ORD-F-146: a faixa não some por 30 min num soluço da rede). Na 1ª leitura, desligada.
 * - A validade é conferida pelo relógio da fonte (`now`, o mesmo da fase do site), e não pelo do aparelho.
 */
import { useEffect, useLayoutEffect, useState } from 'preact/hooks';
import { type PartnerConfig, PartnerConfigSchema, type PartnerPiece } from '../contracts/partner';
import { PartnerSlot } from '../features/partner/PartnerSlot';
import { liveView, now } from '../state/store';
import { firstPaintDone } from './firstPaint';
import { partnerBarState } from './partnerState';

/** O arquivo da peça, na origem do site (sem cache longo: cai na regra "/**" do Hosting, max-age=0). */
export const PARTNER_URL = '/partner/partner.json';
/** Releitura do arquivo (§12.5.5). */
export const PARTNER_REFRESH_MS = 30 * 60_000;
/** Conferência da validade. */
const TICK_MS = 60_000;

/** Pura. A peça da faixa agora, ou null (regras no topo do arquivo). */
export function partnerBarPiece(cfg: PartnerConfig | null, nowMs: number): PartnerPiece | null {
  if (cfg === null || !cfg.enabled) return null;
  const from = Date.parse(cfg.valid_from);
  const until = Date.parse(cfg.valid_until);
  if (!(nowMs >= from && nowMs < until)) return null;
  const piece = cfg.slots.bar;
  if (piece === null) return null;
  if (piece.image && !piece.image.src.startsWith('/partner/')) return null;
  return piece;
}

/** A leitura falhou de passagem (rede, 5xx, resposta cortada): fica a última peça boa. */
export const PARTNER_UNREACHABLE = 'unreachable';
/** Arquivo retirado de propósito (o Hosting responde 404; 403 e 410 valem o mesmo). */
const GONE = new Set([403, 404, 410]);

/** Lê e valida o arquivo: a peça, null (retirada ou recusada) ou PARTNER_UNREACHABLE (falha passageira). */
export async function fetchPartner(
  fetchFn: typeof fetch = fetch,
): Promise<PartnerConfig | null | typeof PARTNER_UNREACHABLE> {
  let body: unknown;
  try {
    const res = await fetchFn(PARTNER_URL, { cache: 'no-cache', credentials: 'omit' });
    if (!res.ok) return GONE.has(res.status) ? null : PARTNER_UNREACHABLE;
    body = await res.json();
  } catch {
    return PARTNER_UNREACHABLE;
  }
  const parsed = PartnerConfigSchema.safeParse(body);
  return parsed.success ? (parsed.data as PartnerConfig) : null;
}

/** O relógio da fonte (o da fase do site, já corrigido pelo `Date` do feed); antes dele, o do aparelho. */
function sourceNow(): number {
  const ms = now.peek().ms;
  return ms > 0 ? ms : Date.now();
}

function whenIdle(fn: () => void): () => void {
  if (typeof requestIdleCallback === 'function') {
    const id = requestIdleCallback(fn, { timeout: 3000 });
    return () => cancelIdleCallback(id);
  }
  const id = setTimeout(fn, 0);
  return () => clearTimeout(id);
}

export function LivePartnerBar({ waitForData = false }: { waitForData?: boolean }) {
  // undefined: ainda não leu; null: leu e não há peça
  const [cfg, setCfg] = useState<PartnerConfig | null | undefined>(undefined);
  const [nowMs, setNowMs] = useState(sourceNow);
  // com a apuração na tela (AppShell, às 17h00 ou aberta na fase `after`), espera o 1º dado; fora dela, não
  const ready = firstPaintDone.value && (!waitForData || liveView.value !== null);
  useEffect(() => {
    if (!ready) return;
    let alive = true;
    let last = Number.NEGATIVE_INFINITY;
    const load = () => {
      last = Date.now();
      void fetchPartner().then((c) => {
        if (!alive) return;
        setCfg((cur) => (c === PARTNER_UNREACHABLE ? (cur ?? null) : c));
        setNowMs(sourceNow());
      });
    };
    const cancel = whenIdle(load);
    const tick = setInterval(() => {
      setNowMs(sourceNow());
      if (Date.now() - last >= PARTNER_REFRESH_MS) load();
    }, TICK_MS);
    return () => {
      alive = false;
      cancel();
      clearInterval(tick);
    };
  }, [ready]);
  const piece = cfg === undefined ? undefined : partnerBarPiece(cfg, nowMs);
  const state = piece === undefined ? 'pending' : piece === null ? 'off' : 'on';
  // logo depois do render (a caixa recolhe no mesmo quadro em que a peça some)
  useLayoutEffect(() => {
    partnerBarState.value = state;
  }, [state]);
  return piece ? <PartnerSlot variant="bar" piece={piece} /> : null;
}
